import { Fragment, type ReactNode } from 'react';
import { marked, type Token, type Tokens } from 'marked';

/**
 * Rendering Markdown di un'informativa.
 *
 * Scelta centrale: si costruiscono **elementi React dai token**, mai una
 * stringa HTML. Non compare alcun `dangerouslySetInnerHTML`, quindi non esiste
 * un percorso attraverso cui il contenuto di un'informativa possa iniettare
 * markup nella pagina mostrata al visitatore (§11.4).
 *
 * La conseguenza è che la sicurezza qui è **per costruzione, non per filtro**:
 * ciò che non è in questa whitelist non viene reso come markup, punto. Un
 * sanitizzatore, per contro, va tenuto aggiornato e sbaglia in silenzio.
 *
 * Il contenuto salvato resta testo semplice: l'hash copre esattamente ciò che
 * una persona legge, e il documento resta ispezionabile in un diff o in una
 * revisione (§6.7, §22).
 */

/** Solo protocolli innocui: niente `javascript:`, `data:` o schemi arbitrari. */
const SAFE_LINK = /^(https?:|mailto:)/i;

function renderInline(tokens: readonly Token[] | undefined, keyPrefix: string): ReactNode {
  if (!tokens) return null;

  return tokens.map((token, index) => {
    const key = `${keyPrefix}-${index}`;

    switch (token.type) {
      case 'strong':
        return <strong key={key}>{renderInline((token as Tokens.Strong).tokens, key)}</strong>;

      case 'em':
        return <em key={key}>{renderInline((token as Tokens.Em).tokens, key)}</em>;

      case 'del':
        return <del key={key}>{renderInline((token as Tokens.Del).tokens, key)}</del>;

      case 'codespan':
        return <code key={key}>{(token as Tokens.Codespan).text}</code>;

      case 'br':
        return <br key={key} />;

      case 'link': {
        const link = token as Tokens.Link;
        // Un link con protocollo non ammesso resta testo: non si silenzia, ma
        // non diventa nemmeno cliccabile.
        if (!SAFE_LINK.test(link.href)) {
          return <Fragment key={key}>{link.text}</Fragment>;
        }
        return (
          <a key={key} href={link.href} target="_blank" rel="noopener noreferrer">
            {renderInline(link.tokens, key)}
          </a>
        );
      }

      case 'image':
        // Un'informativa non ha bisogno di immagini, e caricarne una da un URL
        // esterno contatterebbe un terzo dal dispositivo del visitatore (§17).
        return <Fragment key={key}>{(token as Tokens.Image).text}</Fragment>;

      case 'html':
        // Reso come testo letterale: l'autore vede che non ha avuto effetto,
        // invece di credere di aver inserito markup.
        return <Fragment key={key}>{(token as Tokens.HTML).raw}</Fragment>;

      default:
        return <Fragment key={key}>{(token as Tokens.Text).raw ?? ''}</Fragment>;
    }
  });
}

function renderBlocks(tokens: readonly Token[], keyPrefix = 'b'): ReactNode {
  return tokens.map((token, index) => {
    const key = `${keyPrefix}-${index}`;

    switch (token.type) {
      case 'heading': {
        const heading = token as Tokens.Heading;
        // I livelli partono da h2: l'h1 della pagina è già il titolo del documento.
        const Tag = (['h2', 'h2', 'h3', 'h4', 'h5', 'h6'] as const)[
          Math.min(heading.depth, 6) - 1
        ] as 'h2';
        return <Tag key={key}>{renderInline(heading.tokens, key)}</Tag>;
      }

      case 'paragraph':
        return <p key={key}>{renderInline((token as Tokens.Paragraph).tokens, key)}</p>;

      case 'list': {
        const list = token as Tokens.List;
        const items = list.items.map((item, itemIndex) => (
          <li key={`${key}-${itemIndex}`}>{renderBlocks(item.tokens, `${key}-${itemIndex}`)}</li>
        ));
        return list.ordered ? <ol key={key}>{items}</ol> : <ul key={key}>{items}</ul>;
      }

      case 'blockquote':
        return (
          <blockquote key={key}>
            {renderBlocks((token as Tokens.Blockquote).tokens, key)}
          </blockquote>
        );

      case 'code':
        return (
          <pre key={key}>
            <code>{(token as Tokens.Code).text}</code>
          </pre>
        );

      case 'hr':
        return <hr key={key} />;

      case 'space':
        return null;

      case 'html':
        return <p key={key}>{(token as Tokens.HTML).raw}</p>;

      case 'text': {
        const text = token as Tokens.Text;
        return <p key={key}>{text.tokens ? renderInline(text.tokens, key) : text.text}</p>;
      }

      default:
        return null;
    }
  });
}

export function MarkdownView({ content, className }: { content: string; className?: string }) {
  const tokens = marked.lexer(content);
  return <div className={className}>{renderBlocks(tokens)}</div>;
}

/** Esposta per i test: verifica che nessun markup sopravviva al rendering. */
export function markdownToTokens(content: string): Token[] {
  return marked.lexer(content);
}
